import { useState, useEffect, useCallback } from 'react';
import { Key, Trash2, RefreshCw, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { api } from '../../../api/client';

interface Credential {
  id: number;
  service: string;
  user_id: string;
  label: string;
  app_id: string | null;
  expires_at: string | null;
  has_refresh: boolean;
  status: 'valid' | 'expired' | 'not_connected';
  updated_at: string | null;
}

export function CredentialsTab({ t }: { t: (k: string) => string }) {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [grouped, setGrouped] = useState<Record<string, Credential[]>>({});
  const [stats, setStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshingId, setRefreshingId] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [credRes, statsRes] = await Promise.all([
      api.get<any>('/credentials'),
      api.get<any>('/credentials/stats'),
    ]);
    if (credRes.success && credRes.data) {
      setCredentials((credRes.data as any).credentials || []);
      setGrouped((credRes.data as any).grouped || {});
    }
    if (statsRes.success && statsRes.data) {
      setStats(statsRes.data as any[] || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async (id: number) => {
    setRefreshingId(id);
    setMsg(null);
    try {
      const res = await api.post<any>(`/credentials/refresh/${id}`, {});
      if (res.success) {
        setMsg({ type: 'ok', text: t('settings.credRefreshOk') });
        load();
      } else {
        setMsg({ type: 'err', text: res.error || t('settings.credRefreshError') });
      }
    } catch {
      setMsg({ type: 'err', text: t('settings.credRefreshError') });
    }
    setRefreshingId(null);
    setTimeout(() => setMsg(null), 4000);
  };

  const handleDelete = async (cred: Credential) => {
    if (!confirm(t('settings.credDeleteConfirm'))) return;
    setMsg(null);
    try {
      const res = await api.delete(`/credentials/${cred.id}`);
      if (res.success) {
        setMsg({ type: 'ok', text: t('settings.credDeleteOk') });
        load();
      } else {
        setMsg({ type: 'err', text: res.error || 'Error' });
      }
    } catch {
      setMsg({ type: 'err', text: 'Error' });
    }
    setTimeout(() => setMsg(null), 4000);
  };

  const timeAgo = (date: string | null) => {
    if (!date) return '-';
    const diff = new Date(date).getTime() - Date.now();
    if (diff < 0) {
      const ago = Math.abs(diff);
      const mins = Math.floor(ago / 60000);
      if (mins < 60) return `${mins}m ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours}h ago`;
      return `${Math.floor(hours / 24)}d ago`;
    }
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  const totalCreds = credentials.length;
  const validCreds = credentials.filter(c => c.status === 'valid').length;
  const expiredCreds = credentials.filter(c => c.status === 'expired').length;
  const services = Object.keys(grouped).length;

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-[var(--color-text-muted)]">
        <RefreshCw className="w-4 h-4 animate-spin" /> {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)]">{t('settings.credTitle')}</h2>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">{t('settings.credDesc')}</p>
        </div>
        <button onClick={load} className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Message */}
      {msg && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
          msg.type === 'ok'
            ? 'bg-green-500/10 border border-green-500/30 text-green-600'
            : 'bg-red-500/10 border border-red-500/30 text-red-600'
        }`}>
          {msg.type === 'ok' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
          {msg.text}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: t('settings.credTotal'), value: totalCreds, color: 'text-blue-500', bg: 'bg-blue-500/10' },
          { label: t('settings.credValid'), value: validCreds, color: 'text-green-500', bg: 'bg-green-500/10' },
          { label: t('settings.credExpired'), value: expiredCreds, color: 'text-red-500', bg: 'bg-red-500/10' },
          { label: t('settings.credServices'), value: services, color: 'text-purple-500', bg: 'bg-purple-500/10' },
        ].map(card => (
          <div key={card.label} className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg p-4 text-center">
            <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Credentials by service */}
      {totalCreds === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          <Key size={40} className="mx-auto mb-3 opacity-20" />
          <p>{t('settings.credNoCredentials')}</p>
        </div>
      ) : (
        Object.entries(grouped).map(([service, creds]) => (
          <div key={service} className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg overflow-hidden">
            {/* Service header */}
            <div className="px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-surface)]">
              <h3 className="text-sm font-semibold text-[var(--color-text)] flex items-center gap-2">
                <Key className="w-4 h-4 text-[var(--color-primary)]" />
                {service}
                <span className="text-xs font-normal text-[var(--color-text-muted)]">({creds.length})</span>
              </h3>
            </div>

            {/* Credentials table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left px-4 py-2 text-xs font-medium text-[var(--color-text-secondary)]">{t('settings.credLabel')}</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-[var(--color-text-secondary)]">{t('settings.credAppId')}</th>
                    <th className="text-center px-4 py-2 text-xs font-medium text-[var(--color-text-secondary)]">{t('settings.credStatus')}</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-[var(--color-text-secondary)]">{t('settings.credExpires')}</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-[var(--color-text-secondary)]">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {creds.map((cred) => (
                    <tr key={cred.id} className="hover:bg-[var(--color-surface-hover)] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[var(--color-text)]">{cred.label}</span>
                          <span className="text-[10px] text-[var(--color-text-muted)]">#{cred.user_id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs font-mono text-[var(--color-text-muted)] bg-[var(--color-surface-hover)] px-1.5 py-0.5 rounded">
                          {cred.app_id ? `...${cred.app_id}` : '-'}
                        </code>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          cred.status === 'valid'
                            ? 'bg-green-500/10 text-green-600'
                            : cred.status === 'expired'
                            ? 'bg-red-500/10 text-red-600'
                            : 'bg-gray-500/10 text-[var(--color-text-muted)]'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            cred.status === 'valid' ? 'bg-green-500' : cred.status === 'expired' ? 'bg-red-500' : 'bg-gray-400'
                          }`} />
                          {cred.status === 'valid' ? t('settings.credValid') : cred.status === 'expired' ? t('settings.credExpired') : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--color-text-muted)]">
                        {cred.expires_at ? (
                          <span title={new Date(cred.expires_at).toLocaleString()}>
                            {timeAgo(cred.expires_at)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {cred.has_refresh && (
                            <button
                              onClick={() => handleRefresh(cred.id)}
                              disabled={refreshingId === cred.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 transition-colors"
                            >
                              <RefreshCw className={`w-3 h-3 ${refreshingId === cred.id ? 'animate-spin' : ''}`} />
                              {refreshingId === cred.id ? t('settings.credRefreshing') : t('settings.credRefresh')}
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(cred)}
                            disabled={String(cred.user_id) === '1'}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-lg border border-red-500/30 text-red-500 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title={String(cred.user_id) === '1' ? 'Primary credential cannot be deleted' : t('settings.credDelete')}
                          >
                            <Trash2 className="w-3 h-3" />
                            {t('settings.credDelete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* Add credential note */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-[var(--color-primary-light)]/30 border border-[var(--color-primary)]/20">
        <Info className="w-4 h-4 text-[var(--color-primary)] mt-0.5 shrink-0" />
        <div className="text-xs text-[var(--color-text-secondary)]">
          <p className="font-medium text-[var(--color-text)]">Agregar nueva credencial</p>
          <p className="mt-1">Usa el flujo OAuth de Amazon en <code className="font-mono bg-[var(--color-surface-hover)] px-1 py-0.5 rounded">/api/auth/amazon/login</code> para conectar nuevas credenciales.</p>
        </div>
      </div>
    </div>
  );
}
